import React, { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../api/axiosInstance";

/* ============================================================  HELPERS  */
const fmt = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString("en-IN");
};

/* ============================================================  STATUS BADGE  */
const StatusBadge = ({ status }) => {
  const map = {
    APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
    GREEN:    "bg-blue-100 text-blue-700 border-blue-200",
    RED:      "bg-red-100 text-red-700 border-red-200",
  };
  const labels = { APPROVED: "✅ Approved", GREEN: "🟢 Verified", RED: "🔴 Mismatch" };
  const cls = map[status] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`}>
      {labels[status] || status}
    </span>
  );
};

/* ============================================================  SPINNER  */
const Spinner = ({ size = 5 }) => (
  <div className={`animate-spin rounded-full w-${size} h-${size} border-2 border-blue-500 border-t-transparent`} />
);

/* ============================================================  MAIN  */
export default function OCRPurchaseDashboard() {
  /* ---- list / upload ---- */
  const [invoices, setInvoices]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [activeTab, setActiveTab]     = useState("ALL");
  const [search, setSearch]           = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const fileRef = useRef(null);

  /* ---- detail view ---- */
  const [detailBill, setDetailBill]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* ---- edit modal ---- */
  const [editBill, setEditBill]       = useState(null);
  const [editItems, setEditItems]     = useState([]);
  const [editCharges, setEditCharges] = useState({
    loadingUnloading: "",
    transportation: "",
    transportationGst: "",
    transportationLocalScheme: ""
  });
  const [editSaving, setEditSaving]   = useState(false);
  const [poFetching, setPoFetching]   = useState(false);
  const [poFetchError, setPoFetchError] = useState("");

  /* ---- vendor modal ---- */
  const [vendorModal, setVendorModal] = useState(false);
  const [vendorQ, setVendorQ]         = useState("");
  const [vendorRes, setVendorRes]     = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  /* ============================================================  FETCH LIST  */
  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axiosInstance.get("/mis/purchase/bill/list");
      setInvoices(r.data || []);
    } catch {
      Swal.fire({ icon: "error", title: "Failed to load invoices" });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  /* ============================================================  UPLOAD  */
  const handleUpload = async () => {
    if (!selectedFile) return Swal.fire({ icon: "warning", title: "No file selected" });
    setUploading(true); setProgress(0);
    const tick = setInterval(() => setProgress(p => p < 82 ? p + Math.random() * 10 : p), 700);
    const fd = new FormData();
    fd.append("file", selectedFile);
    try {
      const r = await axiosInstance.post("/mis/purchase/bill/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clearInterval(tick); setProgress(100);
      setTimeout(() => setProgress(0), 900);
      Swal.fire({
        icon: r.data.status === "APPROVED" ? "success" : "warning",
        title: r.data.status === "APPROVED" ? "✅ Bill Verified!" : "⚠️ Mismatch Found",
        html: r.data.mismatchDetails
          ? `<p style="text-align:left;font-size:13px;color:#b91c1c">${r.data.mismatchDetails}</p>`
          : r.data.message,
      });
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadInvoices();
    } catch (err) {
      clearInterval(tick); setProgress(0);
      Swal.fire({ icon: "error", title: "Upload Failed", text: err.response?.data?.message || "Server error" });
    } finally { setUploading(false); }
  };

  /* ============================================================  DELETE  */
  const handleDelete = async (id) => {
    const c = await Swal.fire({ title: "Delete?", icon: "warning", showCancelButton: true, confirmButtonColor: "#ef4444", confirmButtonText: "Delete" });
    if (!c.isConfirmed) return;
    try {
      await axiosInstance.delete(`/mis/purchase/bill/delete/${id}`);
      Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
      if (detailBill?.ID === id) setDetailBill(null);
      loadInvoices();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Delete Failed", text: err.response?.data?.message });
    }
  };

  /* ============================================================  APPROVE  */
  const handleApprove = async (id) => {
    try {
      await axiosInstance.put(`/mis/purchase/bill/approve/${id}`);
      Swal.fire({ icon: "success", title: "Approved!", timer: 1400, showConfirmButton: false });
      if (detailBill?.ID === id) setDetailBill(p => ({ ...p, Status: "APPROVED" }));
      loadInvoices();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Approval Failed", html: `<pre style="text-align:left;font-size:12px;white-space:pre-wrap">${err.response?.data?.message || "Server error"}</pre>` });
    }
  };

  /* ============================================================  VIEW DETAIL  */
  const handleView = async (id) => {
    setDetailLoading(true); setDetailBill(null);
    try {
      const r = await axiosInstance.get(`/mis/purchase/bill/details/${id}`);
      let parsed = {};
      try { parsed = JSON.parse(r.data.ExtractedItems || "{}"); } catch {}
      const items = Array.isArray(parsed) ? parsed : (parsed.items || []);
      setDetailBill({ ...r.data, _items: items, _tax: parsed });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { Swal.fire({ icon: "error", title: "Failed to load details" }); }
    finally { setDetailLoading(false); }
  };

  /* ============================================================  OPEN EDIT  */
  const openEdit = (bill) => {
    let parsed = {};
    try { parsed = JSON.parse(bill.ExtractedItems || "{}"); } catch {}
    const items = Array.isArray(parsed) ? parsed : (parsed.items || []);
    setEditBill({ ...bill, _tax: parsed });
    setEditItems(items.map(it => ({ ...it })));
    setEditCharges({
      loadingUnloading: parsed.loadingUnloading || "",
      transportation: parsed.transportation || "",
      transportationGst: parsed.transportationGst || "",
      transportationLocalScheme: parsed.transportationLocalScheme || ""
    });
    setPoFetchError("");
  };

  /* ============================================================  PO AUTO-FETCH  */
  const fetchPOItems = async () => {
    const poNumber = editBill?.PONumber;
    const partyName = editBill?.PartyName;
    if (!poNumber || poNumber.trim().length < 2) {
      setPoFetchError("Please enter a valid PO number");
      return;
    }

    if (editItems.length > 0) {
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: "This will delete all current extracted items and replace them with PO items.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, fetch and replace!",
        cancelButtonText: "Cancel",
        customClass: {
          confirmButton: 'bg-blue-600 text-white px-4 py-2 rounded-lg mx-2 font-bold shadow-md hover:bg-blue-700',
          cancelButton: 'bg-red-500 text-white px-4 py-2 rounded-lg mx-2 font-bold shadow-md hover:bg-red-600',
          actions: 'flex gap-4 mt-6 mb-2'
        },
        buttonsStyling: false
      });
      if (!confirm.isConfirmed) return;
    }

    setPoFetching(true); setPoFetchError("");
    try {
      const params = new URLSearchParams({ ...(partyName ? { partyName } : {}) });
      const r = await axiosInstance.get(`/mis/purchase/po/items/${encodeURIComponent(poNumber.trim())}?${params}`);
      const poItems = r.data.items || [];
      if (poItems.length === 0) {
        setPoFetchError(`PO "${poNumber}" — no items found`);
        return;
      }
      setEditItems(poItems.map(pi => ({
        name: pi.name || pi.description || "",
        description: pi.description || "",
        qty: pi.qty || 1,
        rate: pi.rate || 0,
        amount: ((parseFloat(pi.qty) || 1) * (parseFloat(pi.rate) || 0)).toFixed(2),
        qtyRowOverride: pi.qtyOverride || "Override"
      })));
      setPoFetchError("");
    } catch {
      setPoFetchError("Failed to fetch PO items");
    } finally { setPoFetching(false); }
  };

  /* ============================================================  EDIT ITEM CHANGE  */
  const handleItemChange = (idx, field, val) => {
    setEditItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      if (field === "qty" || field === "rate") {
        const q = parseFloat(field === "qty" ? val : next[idx].qty) || 0;
        const r = parseFloat(field === "rate" ? val : next[idx].rate) || 0;
        next[idx].amount = (q * r).toFixed(2);
      }
      return next;
    });
  };

  const addItem = () => setEditItems(p => [...p, { name: "", qty: 1, rate: 0, amount: "0.00" }]);
  const removeItem = (idx) => setEditItems(p => p.filter((_, i) => i !== idx));

  /* ============================================================  SAVE EDIT  */
  const handleEditSave = async () => {
    if (!editBill) return;
    setEditSaving(true);
    try {
      const totalAmount = editItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
      await axiosInstance.put(`/mis/purchase/bill/update/${editBill.ID}`, {
        partyName: editBill.PartyName,
        poNumber: editBill.PONumber,
        totalAmount,
        items: editItems,
        status: editBill.Status === "APPROVED" ? "GREEN" : editBill.Status,
        loadingUnloading: editCharges.loadingUnloading,
        transportation: editCharges.transportation,
        transportationGst: editCharges.transportationGst,
        transportationLocalScheme: editCharges.transportationLocalScheme
      });
      Swal.fire({ icon: "success", title: "Saved!", timer: 1200, showConfirmButton: false });
      setEditBill(null);
      loadInvoices();
      if (detailBill) handleView(editBill.ID);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save Failed", text: err.response?.data?.message });
    } finally { setEditSaving(false); }
  };

  /* ============================================================  VENDOR LOOKUP  */
  const handleVendorSearch = async () => {
    if (!vendorQ.trim()) return;
    setVendorLoading(true);
    try {
      const r = await axiosInstance.post("/mis/purchase/vendor/verify", { vendorName: vendorQ });
      setVendorRes(r.data);
    } catch { Swal.fire({ icon: "error", title: "Search failed" }); }
    finally { setVendorLoading(false); }
  };

  /* ============================================================  EXPORT  */
  const handleExport = async () => {
    try {
      let url = "/mis/purchase/bill/export";
      if (selectedIds.length > 0) url += `?ids=${selectedIds.join(",")}`;
      const r = await axiosInstance.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(new Blob([r.data]));
      a.download = `Purchase_Bills_${Date.now()}.xlsx`;
      a.click();
    } catch { Swal.fire({ icon: "error", title: "Export Failed" }); }
  };

  /* ============================================================  FILTERED LIST  */
  const filtered = invoices.filter(inv => {
    const tab = activeTab === "ALL" || inv.Status === activeTab;
    const q = !search ||
      (inv.PartyName || "").toLowerCase().includes(search.toLowerCase()) ||
      (inv.PONumber  || "").toLowerCase().includes(search.toLowerCase());
    return tab && q;
  });

  const kpi = {
    total:    invoices.length,
    approved: invoices.filter(i => i.Status === "APPROVED").length,
    mismatch: invoices.filter(i => i.Status === "RED").length,
    verified: invoices.filter(i => i.Status === "GREEN").length,
  };

  const totalEditAmt = editItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  /* ============================================================  DETAIL VIEW  */
  const b = detailBill || {};
  const items = b._items || [];
  const tax = b._tax || {};

  return (
    <>
      {detailLoading ? (
        <div className="flex items-center justify-center min-h-screen bg-slate-50">
          <div className="text-center space-y-3">
            <Spinner size={10} />
            <p className="text-slate-500 text-sm">Loading bill details…</p>
          </div>
        </div>
      ) : detailBill ? (
        <div className="min-h-screen bg-slate-50 p-6">
        <button onClick={() => setDetailBill(null)}
          className="mb-5 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm hover:shadow transition-all">
          ← Back to List
        </button>

        {/* Bill Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-5">
          <div className="flex flex-wrap justify-between gap-5">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-slate-800">{b.PartyName || "—"}</h2>
                <StatusBadge status={b.Status} />
              </div>
              <p className="text-sm text-slate-500">PO Number: <span className="font-semibold text-slate-700">{b.PONumber || "—"}</span></p>
              <p className="text-sm text-slate-500">Bill Date: {fmtDate(b.BillDate || b.CreatedAt)}</p>
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="text-2xl font-bold text-slate-800">{fmt(b.TotalAmount)}</div>
              <div className="flex gap-2 flex-wrap justify-end">
                <button onClick={() => openEdit(b)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-all">
                  ✏️ Edit
                </button>
                {b.Status !== "APPROVED" && (
                  <button onClick={() => handleApprove(b.ID)}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow hover:shadow-md transition-all">
                    ✅ Approve
                  </button>
                )}
                <button onClick={handleExport}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow hover:shadow-md transition-all">
                  📥 Export Excel
                </button>
              </div>
            </div>
          </div>

          {b.MismatchReasons && b.Status !== "APPROVED" && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 leading-relaxed">
              ⚠️ {b.MismatchReasons}
            </div>
          )}
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-5">
          <h3 className="font-bold text-slate-700 mb-4 text-sm uppercase tracking-wide">Extracted Items</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <th className="px-4 py-3 text-left rounded-l-xl">#</th>
                  <th className="px-4 py-3 text-left">Item</th>
                  <th className="px-4 py-3 text-center">Qty</th>
                  <th className="px-4 py-3 text-center">Rate</th>
                  <th className="px-4 py-3 text-right rounded-r-xl">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.length === 0
                  ? <tr><td colSpan={5} className="text-center py-8 text-slate-400">No items extracted</td></tr>
                  : items.map((it, i) => (
                    <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-slate-800">{it.name || it.description || "—"}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{it.qty}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{fmt(it.rate)}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-800">{fmt(it.amount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tax */}
        {(tax.cgst || tax.sgst || tax.igst) && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-bold text-slate-700 mb-4 text-sm uppercase tracking-wide">Tax Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Taxable Amt", tax.taxableAmount], ["CGST", tax.cgst], ["SGST", tax.sgst], ["IGST", tax.igst]]
                .filter(([, v]) => v > 0)
                .map(([label, val]) => (
                  <div key={label} className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                    <div className="text-xs text-slate-500 mb-1">{label}</div>
                    <div className="font-bold text-slate-800">{fmt(val)}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
      ) : (
      <div className="min-h-screen bg-slate-50 p-6">

      {/* ---- Page Header ---- */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight">🧾 OCR Purchase Bills</h1>
          <p className="text-sm text-slate-500 mt-0.5">AI-powered bill verification · MIS Portal</p>
        </div>
        <button onClick={() => setVendorModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 shadow hover:shadow-md transition-all">
          🔍 Vendor / PO Lookup
        </button>
      </div>

      {/* ---- KPI Cards ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Bills",  val: kpi.total,    color: "from-blue-500 to-indigo-500",   bg: "bg-blue-50",   tx: "text-blue-700"   },
          { label: "Approved",     val: kpi.approved, color: "from-emerald-500 to-teal-500",   bg: "bg-emerald-50", tx: "text-emerald-700" },
          { label: "Mismatch",     val: kpi.mismatch, color: "from-red-500 to-rose-500",       bg: "bg-red-50",    tx: "text-red-700"    },
          { label: "Verified",     val: kpi.verified, color: "from-purple-500 to-violet-500",  bg: "bg-purple-50", tx: "text-purple-700" },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${k.color} flex items-center justify-center`}>
              <span className="text-white text-xl font-bold">{k.val}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 font-medium">{k.label}</div>
              <div className={`text-2xl font-extrabold ${k.tx}`}>{k.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ---- Upload Card ---- */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">AI</span>
          <h2 className="font-bold text-slate-700">Upload New Invoice for OCR</h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <label className="flex-1 flex items-center gap-3 cursor-pointer border-2 border-dashed border-slate-200 hover:border-blue-400 rounded-xl px-4 py-3 transition-all bg-slate-50 hover:bg-blue-50/30">
            <span className="text-2xl">📎</span>
            <div>
              <p className="text-sm font-semibold text-slate-700">{selectedFile ? selectedFile.name : "Click to choose file"}</p>
              <p className="text-xs text-slate-400">PDF, JPG, PNG accepted</p>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
              onChange={e => setSelectedFile(e.target.files[0])} />
          </label>
          <button onClick={handleUpload} disabled={uploading || !selectedFile}
            className={`px-6 py-3 rounded-xl text-sm font-bold text-white shadow transition-all ${
              uploading || !selectedFile
                ? "bg-slate-300 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-lg hover:-translate-y-0.5"
            }`}>
            {uploading ? <span className="flex items-center gap-2"><Spinner size={4} /> Processing…</span> : "🚀 Upload & Verify"}
          </button>
        </div>
        {uploading && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>🤖 AI OCR in progress…</span>
              <span className="font-semibold">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* ---- Filter + Search + Export ---- */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "ALL",      label: `All (${kpi.total})` },
            { key: "APPROVED", label: `✅ Approved (${kpi.approved})` },
            { key: "GREEN",    label: `🟢 Verified (${kpi.verified})` },
            { key: "RED",      label: `🔴 Mismatch (${kpi.mismatch})` },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${
                activeTab === t.key
                  ? "bg-blue-600 text-white border-blue-600 shadow"
                  : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input type="text" placeholder="🔎 Search vendor / PO…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-52" />
          {selectedIds.length > 0 && (
            <span className="text-xs text-slate-500">{selectedIds.length} selected</span>
          )}
          <button onClick={handleExport}
            className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 shadow hover:shadow-md transition-all">
            📥 Export Excel
          </button>
        </div>
      </div>

      {/* ---- Table ---- */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
            <Spinner size={8} /><span className="text-sm">Loading invoices…</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-xs text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3">
                    <input type="checkbox"
                      onChange={e => setSelectedIds(e.target.checked ? filtered.map(i => i.ID) : [])}
                      checked={selectedIds.length === filtered.length && filtered.length > 0}
                      className="rounded" />
                  </th>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-left">PO Number</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-center">Date</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-14 text-slate-400">
                    <div className="text-4xl mb-2">📭</div>No bills found
                  </td></tr>
                ) : filtered.map((inv, idx) => (
                  <tr key={inv.ID} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-4 py-3.5">
                      <input type="checkbox" checked={selectedIds.includes(inv.ID)}
                        onChange={() => setSelectedIds(p => p.includes(inv.ID) ? p.filter(x => x !== inv.ID) : [...p, inv.ID])}
                        className="rounded" />
                    </td>
                    <td className="px-4 py-3.5 text-slate-400 text-xs">{idx + 1}</td>
                    <td className="px-4 py-3.5">
                      <div className="font-semibold text-slate-800 max-w-[180px] truncate">{inv.PartyName || "—"}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{inv.PONumber || "—"}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right font-bold text-slate-800">{fmt(inv.TotalAmount)}</td>
                    <td className="px-4 py-3.5 text-center text-xs text-slate-500">{fmtDate(inv.BillDate || inv.CreatedAt)}</td>
                    <td className="px-4 py-3.5 text-center"><StatusBadge status={inv.Status} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5 justify-center flex-wrap">
                        <button onClick={() => handleView(inv.ID)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-all">
                          👁 View
                        </button>
                        <button onClick={() => openEdit(inv)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 hover:bg-amber-100 transition-all">
                          ✏️ Edit
                        </button>
                        {inv.Status !== "APPROVED" && (
                          <button onClick={() => handleApprove(inv.ID)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 transition-all">
                            ✅
                          </button>
                        )}
                        <button onClick={() => handleDelete(inv.ID)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 border border-red-100 hover:bg-red-100 transition-all">
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    )}

      {/* ============================================================  EDIT MODAL  */}
      {editBill && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto overflow-x-hidden border border-slate-100 flex flex-col">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-blue-500 text-white sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <span className="text-xl">📝</span>
                <h3 className="text-lg font-bold">Manual Edit Mode</h3>
              </div>
              <button onClick={() => setEditBill(null)} className="text-blue-100 hover:text-white w-8 h-8 rounded-full hover:bg-blue-600 flex items-center justify-center text-2xl font-light transition-all">×</button>
            </div>

            <div className="p-6 space-y-6 flex-1 bg-white">
              {/* Row 1: Vendor, PO, Total */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Vendor Name</label>
                  <input value={editBill.PartyName || ""}
                    onChange={e => setEditBill(p => ({ ...p, PartyName: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none focus:border-transparent transition-all font-semibold text-slate-800"
                    placeholder="e.g. MULTIMEDIA PRINT" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide">PO Number</label>
                  <input value={editBill.PONumber || ""}
                    onChange={e => setEditBill(p => ({ ...p, PONumber: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none focus:border-transparent transition-all font-semibold text-slate-800"
                    placeholder="Enter PO number…" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Grand Total (₹)</label>
                  <div className="w-full border border-slate-100 bg-slate-50 rounded-lg px-3 py-2 text-sm font-bold text-blue-700">
                    {totalEditAmt}
                  </div>
                </div>
              </div>

              {/* Row 2: Additional Charges */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <h4 className="text-xs font-bold text-slate-800 mb-4">Additional Charges (Applied to entire bill)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Loading/Unloading + GST (₹)", key: "loadingUnloading" },
                    { label: "Transportation (₹)", key: "transportation" },
                    { label: "Transportation with GST (₹)", key: "transportationGst" },
                    { label: "Transportation(Local)/Scheme(₹)", key: "transportationLocalScheme" }
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-1.5 truncate" title={field.label}>{field.label}</label>
                      <input type="number" step="0.01" value={editCharges[field.key] || ""}
                        onChange={e => setEditCharges(p => ({ ...p, [field.key]: e.target.value }))}
                        className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 focus:outline-none" placeholder="0.00" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Row 3: Items Table */}
              <div>
                <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                  <h4 className="text-sm font-bold text-slate-800">Extracted Items</h4>
                  <div className="flex gap-2">
                    <button onClick={fetchPOItems} disabled={poFetching}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 font-semibold transition-all">
                      {poFetching ? <Spinner size={3} /> : "🔄"} Fetch PO Items
                    </button>
                    <button onClick={addItem}
                      className="text-xs px-3 py-1.5 bg-white text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 font-semibold transition-all">
                      + Add Item
                    </button>
                  </div>
                </div>

                {poFetchError && <p className="text-xs text-red-500 mb-2 font-medium">{poFetchError}</p>}
                {!poFetchError && !poFetching && editBill?.PONumber && editItems.length > 0 && (
                  <p className="text-xs text-emerald-600 mb-2 font-medium">✅ {editItems.length} items loaded from PO.</p>
                )}

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1a1a1a] text-white text-[10px] uppercase tracking-wider text-left">
                          <th className="px-4 py-3 font-bold w-1/3">Item Name / Description</th>
                          <th className="px-4 py-3 font-bold text-center w-24">Quantity</th>
                          <th className="px-4 py-3 font-bold text-center w-24">Rate (₹)</th>
                          <th className="px-4 py-3 font-bold text-right w-28">Amount (₹)</th>
                          <th className="px-4 py-3 font-bold text-center w-28">Qty/Row</th>
                          <th className="px-4 py-3 font-bold text-center w-12">Del</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {editItems.length === 0 ? (
                          <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-sm">No items added yet. Click "+ Add Item" or fetch from PO.</td></tr>
                        ) : editItems.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                            <td className="p-2">
                              <input value={item.name || item.description || ""}
                                onChange={e => handleItemChange(idx, "name", e.target.value)}
                                className="w-full border-none bg-transparent px-2 py-1 text-sm focus:ring-2 focus:ring-blue-100 rounded text-slate-700 font-medium" placeholder="Item name" />
                            </td>
                            <td className="p-2">
                              <input type="number" value={item.qty || ""}
                                onChange={e => handleItemChange(idx, "qty", e.target.value)}
                                className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                            </td>
                            <td className="p-2">
                              <input type="number" value={item.rate || ""}
                                onChange={e => handleItemChange(idx, "rate", e.target.value)}
                                className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                            </td>
                            <td className="p-2">
                              <div className="w-full px-2 py-1.5 text-sm font-bold text-blue-700 text-right">
                                {item.amount}
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              <select value={item.qtyRowOverride || "Override"} onChange={e => handleItemChange(idx, "qtyRowOverride", e.target.value)}
                                className="w-full border border-slate-200 rounded px-1 py-1.5 text-xs text-center focus:ring-2 focus:ring-blue-200 focus:outline-none text-slate-600 bg-slate-50">
                                <option value="Override">Override</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                              </select>
                            </td>
                            <td className="p-2 text-center">
                              <button onClick={() => removeItem(idx)}
                                className="w-7 h-7 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50 transition-all font-bold text-lg">
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
              <button onClick={() => setEditBill(null)}
                className="px-6 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white font-bold hover:bg-slate-100 transition-all">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={editSaving}
                className={`px-6 py-2 rounded-lg text-sm font-bold text-white transition-all ${
                  editSaving ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}>
                {editSaving ? <span className="flex items-center gap-2"><Spinner size={4} /> Saving…</span> : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================  VENDOR MODAL  */}
      {vendorModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto border border-slate-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">🔍 Vendor / PO Lookup</h3>
              <button onClick={() => { setVendorModal(false); setVendorRes(null); setVendorQ(""); }}
                className="text-slate-400 hover:text-slate-700 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-xl transition-all">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-2">
                <input type="text" value={vendorQ} onChange={e => setVendorQ(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleVendorSearch()}
                  placeholder="Vendor name, PO number, or item…"
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none" />
                <button onClick={handleVendorSearch} disabled={vendorLoading}
                  className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 disabled:opacity-60 transition-all">
                  {vendorLoading ? <Spinner size={4} /> : "Search"}
                </button>
              </div>

              {vendorRes && (
                <div className="space-y-4">
                  {vendorRes.matches?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Tally Ledger Matches</h4>
                      {vendorRes.matches.map((m, i) => (
                        <div key={i} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 mb-2">
                          <span className="text-emerald-600 text-sm">✓</span>
                          <span className="text-sm font-medium text-emerald-800">{m}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {vendorRes.poMatches?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">PO Database ({vendorRes.poMatches.length})</h4>
                      {vendorRes.poMatches.map((po, i) => (
                        <div key={i} className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-2">
                          <div className="font-semibold text-blue-800 text-sm">{po.supplierName}</div>
                          <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                            <span className="bg-white border border-slate-200 rounded px-2 py-0.5">PO: {po.poNumber}</span>
                            <span className="bg-white border border-slate-200 rounded px-2 py-0.5">{po.itemName}</span>
                            <span className="bg-white border border-slate-200 rounded px-2 py-0.5">₹{po.rate}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!vendorRes.found && (
                    <div className="text-center py-6 text-slate-400 bg-slate-50 rounded-xl">
                      <div className="text-3xl mb-2">🔍</div>No matches found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
